import React, { FC, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Alert,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { BarcodeMappingDto } from '../../../../app/services/api/orderManagementClient';
import { Trash01 } from '../../../../assets';

type Props = {
  open: boolean;
  mapping: BarcodeMappingDto | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

const BarcodeMappingDeleteModal: FC<Props> = ({ open, mapping, onClose, onConfirm }) => {
  const { t } = useTranslation('barcodeMapping');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setErrorMessage('');
  }, [open]);

  const handleConfirm = async () => {
    setErrorMessage('');
    setIsLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (error: unknown) {
      const message = error && typeof error === 'object' && 'message' in error
        ? (error as Error).message
        : t('unknownError');
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="barcode-mapping-delete-dialog-title"
      open={open}
      maxWidth="sm"
      sx={{ p: 0 }}
    >
      <DialogTitle sx={{ m: 4, p: 4 }}>
        {t('deleteMappingTitle')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.custom.gray[800],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', maxWidth: '450px' }}
      >
        <Grid
          container
          spacing={6}
          alignItems="self-end"
          p={6}
        >
          {errorMessage && (
            <Grid item
              xs={12}>
              <Alert severity="error">{errorMessage}</Alert>
            </Grid>
          )}
          <Typography variant="textMD"
            sx={{ my: 4, mx: 6 }}>
            {t('deleteMappingConfirmation')} {mapping ? mapping?.Barcode : ''}?
          </Typography>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ mt: 4, mb: 4, mr: 4 }}>
        <Button
          variant="secondary"
          size="lg"
          onClick={onClose}
          disabled={isLoading}
          sx={{ padding: '6px 16px' }}
        >
          {t('cancel')}
        </Button>
        <Button
          variant="bad"
          size="lg"
          onClick={handleConfirm}
          disabled={isLoading}
          startIcon={<Trash01 />}
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeMappingDeleteModal;